import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateSite } from "@/api/hooks";
import type { EnvVar } from "@/api/hostingApi";

const NODE_VERSIONS = ["18", "20", "22"];

export function NewSitePage() {
    const navigate = useNavigate();
    const createSite = useCreateSite();

    const [name, setName] = useState("");
    const [subdomain, setSubdomain] = useState("");
    const [gitRepoUrl, setGitRepoUrl] = useState("");
    const [nodeVersion, setNodeVersion] = useState("20");
    const [startCommand, setStartCommand] = useState("");
    const [envVars, setEnvVars] = useState<EnvVar[]>([]);

    const addEnvVar = () => setEnvVars((v) => [...v, { key: "", value: "" }]);
    const updateEnvVar = (i: number, field: keyof EnvVar, value: string) =>
        setEnvVars((v) => v.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));
    const removeEnvVar = (i: number) => setEnvVars((v) => v.filter((_, idx) => idx !== i));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const site = await createSite.mutateAsync({
            name,
            subdomain,
            gitRepoUrl,
            nodeVersion,
            startCommand: startCommand.trim() || undefined,
            envVars: envVars.filter((v) => v.key.trim())
        });
        navigate(`/sites/${site.id}`);
    };

    return (
        <div className="mx-auto max-w-2xl p-6">
            <button
                type="button"
                onClick={() => navigate("/")}
                className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
                <ArrowLeft className="h-4 w-4" /> Back to sites
            </button>

            <h1 className="mb-1 text-xl font-semibold">New Node.js site</h1>
            <p className="mb-6 text-sm text-muted-foreground">
                Athavita clones your repo, runs <code>npm install</code>, and starts it in an isolated
                container. Only Node.js is supported today — PHP and .NET are planned.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                    <label className="mb-1 block text-sm font-medium" htmlFor="name">Site name</label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>

                <div>
                    <label className="mb-1 block text-sm font-medium" htmlFor="subdomain">Subdomain</label>
                    <div className="flex items-center gap-2">
                        <Input
                            id="subdomain"
                            value={subdomain}
                            onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                            pattern="[a-z0-9-]{1,63}"
                            placeholder="my-app"
                            required
                        />
                        <span className="whitespace-nowrap text-sm text-muted-foreground">.hosted.athavita.com</span>
                    </div>
                </div>

                <div>
                    <label className="mb-1 block text-sm font-medium" htmlFor="repo">Git repository URL</label>
                    <Input
                        id="repo"
                        value={gitRepoUrl}
                        onChange={(e) => setGitRepoUrl(e.target.value)}
                        placeholder="https://github.com/you/your-app.git"
                        required
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="mb-1 block text-sm font-medium" htmlFor="node">Node.js version</label>
                        <select
                            id="node"
                            value={nodeVersion}
                            onChange={(e) => setNodeVersion(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            {NODE_VERSIONS.map((v) => (
                                <option key={v} value={v}>{v}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium" htmlFor="start">
                            Start command <span className="text-muted-foreground">(optional)</span>
                        </label>
                        <Input
                            id="start"
                            value={startCommand}
                            onChange={(e) => setStartCommand(e.target.value)}
                            placeholder="npm start"
                        />
                    </div>
                </div>

                <div>
                    <div className="mb-1 flex items-center justify-between">
                        <label className="text-sm font-medium">Environment variables</label>
                        <Button type="button" variant="outline" size="sm" onClick={addEnvVar}>
                            <Plus className="h-3.5 w-3.5" /> Add
                        </Button>
                    </div>
                    <div className="flex flex-col gap-2">
                        {envVars.map((v, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <Input
                                    placeholder="KEY"
                                    value={v.key}
                                    onChange={(e) => updateEnvVar(i, "key", e.target.value)}
                                />
                                <Input
                                    placeholder="value"
                                    value={v.value}
                                    onChange={(e) => updateEnvVar(i, "value", e.target.value)}
                                />
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeEnvVar(i)}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>

                {createSite.error && (
                    <p className="text-sm text-destructive">{(createSite.error as Error).message}</p>
                )}

                <Button type="submit" disabled={createSite.isPending}>
                    {createSite.isPending ? "Creating…" : "Create site"}
                </Button>
            </form>
        </div>
    );
}
